import { resetState, setBookingType } from '@src/components/OMS/ManualDealTradeView/ManualDealSlice';
import { openView } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { resolveUsersSentinel } from '@src/hooks/filters/useUsersFilter';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { useUser } from '@src/hooks/useUser';
import { useAppStateDispatch } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import { isForwardDeal, type ForwardDealInfo, type OTCDealData, type VanillaOptionDealInfo } from '@src/types/OTC';
import type { MinimalSubscriptionResponse } from '@talos/kyoko';
import {
  ACTION,
  BlotterDensity,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEAL,
  DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
  ExportMenuItems,
  filterByColumnMainMenuItems,
  FormControlSizes,
  IconName,
  isGridApiReady,
  MixpanelEvent,
  removeEmptyFilters,
  SubProductTypeEnum,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useConstant,
  useDateRangeFilter,
  useMixpanel,
  usePersistedBlotterTable,
  useSecuritiesContext,
  useWsBlotterTable,
  type BlotterTableFilter,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams, SelectionChangedEvent } from 'ag-grid-enterprise';
import { useCallback, useMemo } from 'react';
import { pipe } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { INSTRUMENT_INSPECTOR_DRAWER_TITLE, useInstrumentInspectorDrawer } from '../InstrumentInspectorDrawer';
import type { DealBlotterRequest } from '../otcBlotterTypes';
import { getForwardPriceCurrencyForSymbol } from './getForwardPriceCurrencyForSymbol';
import { useOTCDerivativesColumns } from './useOTCDerivativesColumns';
import { colIDToFilterBuilderKey, onlyServerFilterKeys, useOTCDerivativesFilter } from './useOTCDerivativesFilter';
import { useOTCDerivativesMenu } from './useOTCDerivativesMenu';

interface FilteredOTCDerivativesParams {
  /** ID for common blotter type persistence */
  blotterPersistID: string;
  tabLabel?: string;
  defaultFilter: BlotterTableFilter;
  onCloneTab?: () => void;
  initialIsOpen?: boolean;
  /** Whether to persist the filter, defaults to true */
  persistFilter?: boolean;
}

export const FilteredOTCDerivatives = function FilteredOTCDerivatives({
  blotterPersistID,
  tabLabel,
  defaultFilter,
  onCloneTab,
  initialIsOpen,
  persistFilter,
}: FilteredOTCDerivativesParams) {
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const { isAuthorized } = useRoleAuth();
  const canSubmitOrder = isAuthorized(ACTION.SUBMIT_ORDER);
  const { Name: currentUserName } = useUser();

  const { securitiesBySymbol } = useSecuritiesContext();
  const { enableOTCDeltaStrikeSupport, enableOTCPhysicalSettlement } = useFeatureFlag();

  // Stable identity for normalizeDealDataPipe, protecting securitiesBySymbol
  const getForwardPriceCurrencyForSymbolRef = useCallbackRef((symbol: string | undefined) =>
    getForwardPriceCurrencyForSymbol(symbol, securitiesBySymbol)
  );

  const columns = useOTCDerivativesColumns();

  const persistedBlotterTable = usePersistedBlotterTable(
    blotterPersistID,
    { filter: defaultFilter },
    { persistFilter }
  );

  const filteredOTCDerivatives = useOTCDerivativesFilter({
    persistedBlotterTable,
    securitiesBySymbol,
    enableOTCDeltaStrikeSupport,
    enableOTCPhysicalSettlement,
  });

  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filteredOTCDerivatives;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const { openClause } = filterBuilderAccordion;

  const getExtraMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams) => {
      return filterByColumnMainMenuItems({
        params,
        colIDToFilterBuilderKey,
        openClause,
        mixpanel,
      });
    },
    [mixpanel, openClause]
  );

  const { openInstrumentInspectorDrawer } = useInstrumentInspectorDrawer();

  const openOTCInstrumentInspectorDrawer = useCallback(
    (deal: OTCDealData, onlyIfOpen?: boolean) =>
      openInstrumentInspectorDrawer(deal, INSTRUMENT_INSPECTOR_DRAWER_TITLE, onlyIfOpen),
    [openInstrumentInspectorDrawer]
  );

  const handleBookOTC = useCallback(() => {
    mixpanel.track(MixpanelEvent.OpenManualTradeForm);
    dispatch(resetState());
    dispatch(setBookingType('forward'));
    dispatch(openView(OMSView.ManualDealTradeForm));
  }, [mixpanel, dispatch]);

  const otcMenu = useOTCDerivativesMenu({
    openClause,
    filterableProperties: filterBuilderProps.properties,
    mixpanel,
    openOTCInstrumentInspectorDrawer,
  });

  const onSelectionChanged = useCallback(
    ({ api }: SelectionChangedEvent<OTCDealData>) => {
      if (!isGridApiReady(api)) {
        return;
      }
      const rows = api.getSelectedRows();
      if (rows.length === 1) {
        openOTCInstrumentInspectorDrawer(rows[0], true);
      }
    },
    [openOTCInstrumentInspectorDrawer]
  );

  const columnsWithMenu = useMemo(() => {
    return [...columns, ...otcMenu.columns];
  }, [columns, otcMenu.columns]);

  const blotterTag = useConstant(`FILTERED_OTC_${uuid()}`);

  const request = useMemo(
    () =>
      ({
        name: DEAL,
        tag: blotterTag,
        sort_by: '-TradeTime',
        SubProductTypes: [SubProductTypeEnum.OTCForward, SubProductTypeEnum.OTCVanillaOption],
        ...removeEmptyFilters(
          onlyServerFilterKeys({
            ...filteredOTCDerivatives.filter,
            Users: resolveUsersSentinel(filteredOTCDerivatives.filter.Users, currentUserName),
          })
        ),
      }) satisfies DealBlotterRequest,
    [blotterTag, filteredOTCDerivatives.filter, currentUserName]
  );

  // Normalize deal data: clear InitialMarginAmount if InitialMarginAsset (a.k.a. InitialMarginCurrency) is not set;
  // set forwardPriceCurrency (client) from product QuoteAsset for forwards so the Price column can read it directly.
  const normalizeDealDataPipe = useMemo(
    () =>
      pipe(
        map(
          (response: MinimalSubscriptionResponse<OTCDealData>): MinimalSubscriptionResponse<OTCDealData> => ({
            ...response,
            data: response.data.map(deal => {
              const dealInfo = deal.DealInfo as ForwardDealInfo | VanillaOptionDealInfo;
              // Temporary fix to avoid having InitialMarginAmount show improperly
              if (dealInfo && !dealInfo.InitialMarginAsset && dealInfo.InitialMarginAmount) {
                dealInfo.InitialMarginAmount = undefined;
              }
              if (isForwardDeal(deal)) {
                deal.DealInfo.forwardPriceCurrency = getForwardPriceCurrencyForSymbolRef(deal.Symbol);
              }
              return deal;
            }),
          })
        )
      ),
    [getForwardPriceCurrencyForSymbolRef]
  );

  const blotterTable = useWsBlotterTable({
    request,
    rowID: 'ID',
    columns: columnsWithMenu,
    clientLocalFilter,
    getExtraMainMenuItems,
    persistence: persistedBlotterTable,
    pipe: normalizeDealDataPipe,
    density: BlotterDensity.Compact,
    contextMenuOptions: {
      getCustomContextMenuItems: otcMenu.getCustomContextMenuItems,
      dataDisplayCallback: otcMenu.dataDisplayCallback,
    },
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
      onSelectionChanged,
      onRowDoubleClicked: ({ data }) => {
        data && openOTCInstrumentInspectorDrawer(data);
      },
    },
    exportOptions: {
      name: 'OTCDerivatives',
      tabLabel: tabLabel,
    },
  });

  const dateRangeFilter = useDateRangeFilter(filteredOTCDerivatives.filter, filteredOTCDerivatives.changeFilter);

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        {...dateRangeFilter}
        quickFilterPrefix={
          canSubmitOrder && (
            <Button
              startIcon={IconName.DocumentAdd}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              onClick={handleBookOTC}
            >
              Book OTC
            </Button>
          )
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {otcMenu.dialogs}
    </>
  );
};
